import * as Sentry from '@sentry/browser'
import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'

import { kMatterBasePath } from '@/constants/constants-api-paths'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { AddMatterMembersResponseSchema, type AddMatterMembersResponse, type MatterAssignableRole } from '@/matter/components/manage-members/schemas'
import { getResponseDebugContext, getTopLevelKeys, readResponseJson } from '@/util/api/response-body'
import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'

export async function postAddMatterMembers(
  matterId: string,
  userIds: string[],
  matterRole: MatterAssignableRole = 'collaborator'
): Promise<AddMatterMembersResponse> {
  const transactionId = nanoid()
  try {
    const token = await getAuth().currentUser?.getIdToken()
    if (!token) throw new Error("Could not retrieve user's authentication token.")

    const apiUrl = `${kPaxtonAppApiRESTBaseUrl()}${kMatterBasePath}${matterId}/members`
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json; charset=UTF-8',
        'X-Transaction-Id': transactionId,
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ user_ids: userIds, matter_role: matterRole }),
    })
    const { data, parseError } = await readResponseJson(response)

    if (!response.ok) {
      const detail = (data as { detail?: unknown } | undefined)?.detail
      if (![400, 403, 404, 409].includes(response.status)) {
        Sentry.withScope((scope) => {
          scope.setTags({ transaction_id: transactionId })
          Sentry.captureException(new Error(`Add matter members failed - status: ${response.status}`), {
            extra: getResponseDebugContext(response, apiUrl),
          })
        })
      }
      throw new ApiError(response.status, `Add matter members failed - status: ${response.status}`, detail)
    }

    if (parseError) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: transactionId })
        Sentry.captureException(new Error('Add matter members response JSON parse failed'), {
          extra: getResponseDebugContext(response, apiUrl),
        })
      })
      throw new ApiResponsePayloadError(response.status, 'Invalid add matter members response JSON')
    }

    const parsed = AddMatterMembersResponseSchema.safeParse(data)
    if (!parsed.success) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: transactionId })
        Sentry.captureException(new Error('Add matter members response schema validation failed'), {
          extra: {
            ...getResponseDebugContext(response, apiUrl),
            responseKeys: getTopLevelKeys(data),
            issues: parsed.error.issues.map(({ code, path }) => ({ code, path })),
          },
        })
      })
      throw new ApiResponsePayloadError(response.status, 'Invalid add matter members response shape', parsed.error.issues)
    }
    return parsed.data
  } catch (error) {
    if (!(error instanceof ApiError)) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: transactionId })
        Sentry.captureException(error)
      })
    }
    throw error
  }
}
